import Async from 'react-select/async';
import Select from 'react-select';
import debounce from 'lodash/debounce';
import { useState, useEffect, useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';
import { toast } from 'react-toastify';
import { API } from '../../API';
import { showRequestError } from '../../utils/requestError';
import Modal from '../../components/Modal';
import { Loader } from '../../components/Loader/Loader';
import styles from './GroupsPage.module.scss';
import Switch from "../../components/Switch";
import {useCurrentUser} from "../../contexts/CurrentUser";

export const GroupEditModal = ({editModal, handleEdit, clearEdit, mode = 'edit', onCreated}) => {
  const { t } = useTranslation();
  const [currentUser] = useCurrentUser();
  const groupId = editModal?.group?.id;
  const [group, setGroup] = useState(null);
  const [name, setName] = useState('');
  const [description, setDescription] = useState('');
  const [privacySettings, setPrivacySettings] = useState('PRIVATE');
  const [workspaces, setWorkspaces] = useState(null);
  const [selectedWorkspaces, setSelectedWorkspaces] = useState([]);
  const [error, setError] = useState(null);
  const [isSaving, setIsSaving] = useState(false);

  useEffect(() => {
    if (mode !== 'edit') return;
    API.fetch('GET', `/API/v1/groups/${groupId}`)
      .then((data) => {
        setGroup(data)
        setName(data?.name)
        setDescription(data?.description || '')
        setPrivacySettings(data?.visibility ? data?.visibility : 'PRIVATE')
        setSelectedWorkspaces(data?.workspace_ids)
      })
      .catch((error) => {
        setError(error.message);
        showRequestError(error, t('Actor group'));
      });
  }, [groupId]);

  const fetchAllWorkspaces = useCallback(
    () => {
      if (!currentUser?.is_super_admin) {
        return;
      }
      const urlParams = new URLSearchParams();

      urlParams.set('size', '20');
      urlParams.set('sorting', '-id');

      API.fetch('GET', `/API/v1/workspaces?${urlParams.toString()}`).then(
        (data) => {
          let workspaces = data.objects
            .map((workspace) => {
              return { value: workspace.id, label: workspace.name };
            });
          setWorkspaces(workspaces);
        },
      ).catch((e) => showRequestError(e, t('Workspaces')));
    },
    [],
  );

  useEffect(fetchAllWorkspaces, [fetchAllWorkspaces]);

  const fetchWorkspaces = useMemo(
    () =>
      debounce((inputValue, callback) => {
        if (!currentUser?.is_super_admin) {
          return;
        }
        const urlParams = new URLSearchParams();

        if (inputValue) {
          urlParams.set('q', inputValue);
        }

        API.fetch('GET', `/API/v1/workspaces?${urlParams.toString()}`).then(
          (data) => {
            callback(
              data.objects
                .sort((a, b) => (a.name > b.name ? 1 : -1))
                .map((workspace) => {
                  return { value: workspace.id, label: workspace.name };
                }),
            );
          },
        ).catch((e) => showRequestError(e, t('Workspaces')));
      }, 1000),
    [],
  );

  const workspacesPromiseOptions = (inputValue) => {
    if (!inputValue) {
      return [];
    }
    return new Promise((resolve) => {
      fetchWorkspaces(inputValue, resolve);
    });
  };

  const fetchSelectedWorkspaces = (selectedWorkspaces) => {
    if (!currentUser?.is_super_admin) {
      return;
    }
    if (mode !== 'edit' || !selectedWorkspaces || selectedWorkspaces?.length === 0) {
      return [];
    }
    const urlParams = new URLSearchParams();
    selectedWorkspaces.forEach((workspace) => {
      urlParams.append('ids', workspace);
    });

    API.fetch('GET', `/API/v1/workspaces?${urlParams.toString()}`).then(
      (data) => {
        let workspaces = data.objects
          .sort((a, b) => (a.name > b.name ? 1 : -1))
          .map((workspace) => {
            return { value: workspace.id, label: workspace.name };
          });
        setSelectedWorkspaces(workspaces);
      },
    ).catch((e) => showRequestError(e, t('Workspaces')));
  };

  useEffect(() => {
    fetchSelectedWorkspaces(group?.workspace_ids);
  }, [group]);

  const handleSubmit = (e) => {
    e.preventDefault();
    if (isSaving) return;

    if (name.trim().length === 0) {
      setError('Please fill in this field');
      return;
    }

    setError(null);
    setIsSaving(true);

    if (mode === 'create') {
      API.fetch('POST', `/API/v1/groups`, null, {
        name,
        description,
        visibility: privacySettings,
        workspace_ids: selectedWorkspaces?.map((w) => w.value),
      }).then((data) => {
        toast.success(t('Group created'));
        clearEdit();
        onCreated(data.id);
      }).catch((e) => showRequestError(e, t('Create group')))
        .finally(() => setIsSaving(false));
    } else {
      // handleEdit returns the parent's PATCH promise so the modal can report
      // the outcome and only close once the save actually succeeded
      Promise.resolve(handleEdit(name, editModal.group, privacySettings, selectedWorkspaces, description))
        .then(() => {
          toast.success(t('Group info saved'));
          clearEdit();
        })
        .catch((e) => showRequestError(e, t('Edit group')))
        .finally(() => setIsSaving(false));
    }
  }

  const privacySettingsOptions = [
    {value: 'PRIVATE', label: t('private_female', {context: 'female'})},
    {value: 'PUBLIC', label: t('public_female', {context: 'female'})},
    {value: 'SHARABLE', label: t('sharable_female', {context: 'female'})},
  ];

  return(
    <Modal
      isVisible={editModal.isOpen}
      title={mode === 'create' ? t('Create actor group') : t('Edit actor group')}
      content={
        <form method="post" onSubmit={e => handleSubmit(e)} >
          <div className={error ? 'form-element error' : 'form-element'}>
            <label htmlFor="name">{t('Name')}</label>
            <input
              className=""
              id="name"
              name="name"
              type="text"
              value={name}
              onChange={(e) => setName(e.target.value)}
              required
            />
          </div>
          <div className="form-element">
            <label htmlFor="description">{t('Description')}</label>
            <textarea
              id="description"
              name="description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
            />
          </div>
          {currentUser?.is_super_admin && (
            <div className="form-element">
              <label className={styles.label} htmlFor="privacy-settings">
                {t('Privacy settings')}
              </label>
              <Select
                id="privacy-settings"
                name="privacy-settings"
                options={privacySettingsOptions}
                value={privacySettingsOptions.find(
                  (option) => option.value === privacySettings,
                )}
                onChange={(option) => setPrivacySettings(option.value)}
              />
            </div>
          )}

          {currentUser?.is_super_admin && privacySettings === 'SHARABLE' && (
            <div className="form-element">
              <label className={styles.label} htmlFor="workspaces">
                {t('Workspaces')}
              </label>
              <Async
                cacheOptions
                defaultOptions={workspaces}
                isMulti={true}
                isClearable={true}
                loadOptions={workspacesPromiseOptions}
                value={selectedWorkspaces}
                placeholder={t('Please enter at least 1 symbol to search')}
                noOptionsMessage={() => t('Please enter at least 1 symbol')}
                loadingMessage={() => t('Loading...')}
                id='workspaces'
                onChange={(options) => {
                  setSelectedWorkspaces(options);
                }}
                required={currentUser?.is_super_admin && privacySettings === 'SHARABLE'}
              />
            </div>
          )}
          <div className="error-message">{t(error)}</div>
        </form>
      }
      footer={
        <>
          <button
            type="button"
            className="new-button"
            disabled={isSaving}
            aria-busy={isSaving}
            onClick={(e) => handleSubmit(e)}
          >
            {isSaving
              ? (mode === 'create' ? t('Creating...') : t('Saving...'))
              : (mode === 'create' ? t('Create') : t('Save'))}
          </button>
          <button type="button" className="btn-reset" disabled={isSaving} onClick={() => clearEdit()}>
            {t('Cancel')}
          </button>
        </>
      }
      onClose={clearEdit}
    />
  )
}